/**
 * Pricing Options — editor component.
 *
 * The canvas draws the same radios the front end does, and clicking one really
 * does switch which price the plans show — because the rule that does the
 * switching is a `:has()` selector in the block's own stylesheet, which the
 * editor loads too. There is no preview state to keep in sync here, and no
 * editor-only copy of the mechanism to drift from the real one.
 *
 * The inputs are uncontrolled on purpose. React setting `checked` every render
 * would undo the reader's click the moment anything else on the block changed;
 * the DOM is the right owner of a preview that is meant to be transient.
 */
import { __ } from '@wordpress/i18n';
import {
  useBlockProps,
  InspectorControls,
  store as blockEditorStore,
} from '@wordpress/block-editor';
import { useSelect, useDispatch } from '@wordpress/data';

import OptionsSettings from './OptionsSettings';
import { MAX_PRICING_OPTIONS } from '../../_shared/pricing-defaults';
import { resolvePresetValue } from '../../../utils/helpers';

export default function Edit({ attributes, setAttributes, context, clientId }) {
  const { optionLayout, optionAlign, shape, accentColor, activeTextColor, heading } =
    attributes;

  const options = (context['brandy/pricingTerms'] || []).slice(
    0,
    MAX_PRICING_OPTIONS
  );
  const defaultOption = Math.max(
    0,
    Math.min(options.length - 1, context['brandy/pricingDefaultTerm'] ?? 0)
  );

  /*
   * The list belongs to the table, so the sidebar writes to the table. Two
   * chooser blocks in one table therefore edit the same list, which is the
   * only coherent answer — they drive the same prices.
   */
  const tableClientId = useSelect(
    (select) =>
      select(blockEditorStore).getBlockParentsByBlockName(
        clientId,
        'brandy/pricing-table',
        true
      )[0],
    [clientId]
  );

  const { updateBlockAttributes } = useDispatch(blockEditorStore);

  const setTableAttributes = (patch) => {
    if (tableClientId) {
      updateBlockAttributes(tableClientId, patch);
    }
  };

  const blockProps = useBlockProps({
    className: [
      'brandy-pricing-options',
      `is-options-${optionLayout || 'segmented'}`,
      `has-options-${optionAlign || 'center'}`,
      `is-shape-${shape || 'pill'}`,
    ].join(' '),
    style: {
      '--pricing-options-count': options.length,
      // Expanded exactly as render.php does via Helpers::get_ref_color(), so a
      // palette pick looks the same in the canvas as on the frontend.
      ...(accentColor
        ? { '--pricing-option-accent': resolvePresetValue(accentColor) }
        : {}),
      ...(activeTextColor
        ? { '--pricing-option-active-text': resolvePresetValue(activeTextColor) }
        : {}),
    },
  });

  /*
   * Named after the table so every chooser inside it shares one group, and so
   * two tables on the same page never check each other's radios. render.php
   * rewrites the name server side for the same reason.
   */
  const groupName = `brandy-pricing-option-${tableClientId || clientId}`;

  return (
    <>
      <InspectorControls>
        <OptionsSettings
          attributes={attributes}
          setAttributes={setAttributes}
          options={options}
          defaultOption={defaultOption}
          setTableAttributes={setTableAttributes}
        />
      </InspectorControls>

      <div {...blockProps}>
        {options.length < 2 ? (
          <span className="brandy-pricing-options__empty">
            {__('Add a second option in the sidebar.', 'brandy-blocks')}
          </span>
        ) : (
          <>
            {heading && (
              <span className="brandy-pricing-options__heading">{heading}</span>
            )}

            <div className="brandy-pricing-options__track" role="radiogroup">
              {options.map((option, index) => (
                <label
                  key={index}
                  className={`brandy-pricing-options__option brandy-pricing-options__option--${index}`}
                >
                  <input
                    // Remounts when the default moves, which is the only time
                    // the DOM's own selection should be overruled.
                    key={`${index}-${defaultOption}`}
                    type="radio"
                    className={`brandy-pricing-options__input brandy-pricing-options__input--${index}`}
                    name={groupName}
                    defaultChecked={index === defaultOption}
                  />
                  <span
                    className="brandy-pricing-options__marker"
                    aria-hidden="true"
                  />
                  <span className="brandy-pricing-options__text">
                    {option.label}
                  </span>
                  {option.badge && (
                    <span className="brandy-pricing-options__badge">
                      {option.badge}
                    </span>
                  )}
                </label>
              ))}
              <span
                className="brandy-pricing-options__thumb"
                aria-hidden="true"
              />
            </div>
          </>
        )}
      </div>
    </>
  );
}
